/**
 * Phase 0 host for the server→client message bus: installs the bus, runs the
 * idle ping, and renders the generic `notification` (and `duel_result`) handlers
 * as toasts. Toasts are suppressed in test mode (mid-solve / lobby / live duel)
 * and flushed on exit — so e.g. the "opponent joined" toast doesn't double up
 * with the lobby roster. Richer per-type handlers (duel_request popup, presence)
 * register elsewhere. Mount once inside the signed-in shell.
 *
 * See docs/FeatureDesigns/duels-invite-smoothing-plan.md.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ServerMessage } from '@shared-ui/api';
import { installMessageBus, registerMessageHandler } from '@shared-ui/messages';
import { isTestModePath } from './testMode';
import { useMessagePing } from './useMessagePing';
import { appHref } from '@shared-ui/tenant';

interface Toast { id: string; title: string; body: string; actionUrl?: string }

export function ServerMessages() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const location = useLocation();
  const testRef = useRef(false);
  testRef.current = isTestModePath(location.pathname);
  const pendingRef = useRef<Toast[]>([]);
  useMessagePing();

  const show = (t: Toast) => {
    setToasts(prev => [...prev, t].slice(-4));
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), 6000);
  };

  useEffect(() => {
    installMessageBus();
    const toToast = (m: ServerMessage): Toast => {
      const p = (m.payload ?? {}) as { title?: string; body?: string; actionUrl?: string };
      return { id: m.id, title: p.title || 'Notification', body: p.body || '', actionUrl: p.actionUrl };
    };
    const handle = (m: ServerMessage) => {
      const t = toToast(m);
      if (testRef.current) pendingRef.current.push(t);   // queue while mid-solve
      else show(t);
    };
    const offs = [
      registerMessageHandler('notification', handle),
      registerMessageHandler('duel_result', handle),
    ];
    return () => { for (const off of offs) off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flush queued toasts when leaving test mode.
  useEffect(() => {
    if (!isTestModePath(location.pathname) && pendingRef.current.length > 0) {
      const items = pendingRef.current;
      pendingRef.current = [];
      items.forEach(show);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (toasts.length === 0) return null;

  return (
    <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 340 }}>
      {toasts.map(t => (
        <div
          key={t.id}
          onClick={() => { if (t.actionUrl) window.location.assign(t.actionUrl.startsWith('/') ? appHref(t.actionUrl) : t.actionUrl); }}
          style={{
            background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1c1f33)',
            border: '1px solid var(--border-color, #e7e9f0)', borderRadius: 12,
            padding: '12px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            cursor: t.actionUrl ? 'pointer' : 'default',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14 }}>{t.title}</div>
          {t.body && <div style={{ fontSize: 13, color: 'var(--text-secondary, #5a5f72)', marginTop: 2 }}>{t.body}</div>}
        </div>
      ))}
    </div>
  );
}
